/**
 * The drawing toolbar.
 *
 * Deliberately thin: a list of tools, a selection, and two destructive actions.
 * All of the interesting behaviour is in `DrawingLayer` and `@aegis/core`, so a
 * redesign can restructure this file entirely — move it to a rail, turn it into
 * a popover, regroup the tools — without touching anything that decides how a
 * drawing behaves.
 *
 * Keyboard shortcuts follow the ones traders already have in their fingers from
 * other platforms: T for trend, H for horizontal, F for Fibonacci, Escape back
 * to the cursor. Muscle memory is worth more here than internal consistency.
 */
import type { IconName } from '@aegis/ui';
import { Icon } from './Icon.js';
import { useEffect } from 'react';
import type { JSX } from 'react';
import type { Tool } from './DrawingLayer.js';

interface ToolSpec {
  readonly id: Tool;
  readonly label: string;
  readonly key: string;
  /** A 24x24 path. Stroked with currentColor, so the theme owns the colour. */
  readonly icon: IconName;
}

const TOOLS: readonly ToolSpec[] = [
  { id: 'cursor', label: 'Select', key: 'v', icon: 'cursor' },
  { id: 'trendline', label: 'Trend line', key: 't', icon: 'trendline' },
  { id: 'horizontal', label: 'Horizontal line', key: 'h', icon: 'horizontal' },
  { id: 'vertical', label: 'Vertical line', key: 'i', icon: 'vertical' },
  { id: 'ray', label: 'Ray', key: 'r', icon: 'ray' },
  { id: 'rectangle', label: 'Rectangle', key: 'b', icon: 'rectangle' },
  { id: 'fib', label: 'Fibonacci retracement', key: 'f', icon: 'fib' },
  { id: 'measure', label: 'Measure', key: 'm', icon: 'measure' },
  { id: 'text', label: 'Text note', key: 'x', icon: 'text' },
];

export function DrawingToolbar({
  tool, onTool, count, limit, selectedId, onDelete, onClear,
}: {
  tool: Tool;
  onTool(t: Tool): void;
  count: number;
  limit: number | null;
  selectedId: string | null;
  onDelete(): void;
  onClear(): void;
}): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      // Never steal a key from a form. A user typing "t" in a journal note is
      // not asking for the trend tool.
      const el = e.target as HTMLElement | null;
      if (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === 'Escape') { onTool('cursor'); return; }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
        e.preventDefault();
        onDelete();
        return;
      }
      const match = TOOLS.find((t) => t.key === e.key.toLowerCase());
      if (match) onTool(match.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onTool, onDelete, selectedId]);

  const atLimit = limit !== null && count >= limit;

  return (
    <div className="draw-toolbar" role="toolbar" aria-label="Drawing tools">
      {TOOLS.map((t) => (
        <button
          key={t.id}
          className={`draw-tool compact ${tool === t.id ? 'on' : ''}`}
          onClick={() => onTool(t.id)}
          title={`${t.label} (${t.key.toUpperCase()})`}
          aria-label={t.label}
          aria-pressed={tool === t.id}
          disabled={atLimit && t.id !== 'cursor'}
        >
          <Icon name={t.icon} size="sm" />
        </button>
      ))}

      <div className="draw-toolbar-spacer" />

      {selectedId && (
        <button className="draw-tool compact" onClick={onDelete} title="Delete (Del)" aria-label="Delete selected">
          <Icon name="trash" size="sm" />
        </button>
      )}

      {count > 0 && (
        <button className="draw-tool compact" onClick={onClear} title="Clear this chart" aria-label="Clear all drawings">
          <Icon name="close" size="sm" />
        </button>
      )}

      {atLimit && (
        <span className="draw-limit" title="Delete some to draw more">
          {count}/{limit}
        </span>
      )}
    </div>
  );
}
